import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ProductCarousel from '../../components/ProductCarousel/ProductCarousel';
import { Product } from '../../types';
import { productsAPI } from '../../services/api';
import './HomePage.css';

const HomePage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadData = async () => {
            try {
                const productsResponse = await productsAPI.getAll({ language: i18n.language });

                // Преобразуем данные продуктов в нужный формат
                const products = productsResponse.data.data.map((p: any) => ({
                    id: p.id.toString(),
                    name: { [i18n.language]: p.name },
                    description: { [i18n.language]: p.description },
                    category: p.category,
                    images: Array.isArray(p.images) ? p.images : [],
                    featured: p.display_order <= 3,
                    price: p.price
                }));

                setFeaturedProducts(products);
            } catch (error) {
                console.error('Error loading data:', error);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [i18n.language]);

    const handleProductClick = (product: Product) => {
        // Yandex Metrika goal
        if (window.ym) {
            window.ym(12345678, 'reachGoal', 'product_view');
        }
        navigate(`/products/${product.id}`);
    };

    return (
        <div className="home-page">
            {/* Hero Section */}
            <motion.section
                className="hero-section"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
            >
                <div className="hero-content">
                    <motion.h1
                        className="hero-title"
                        initial={{ y: 30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.2, duration: 0.8 }}
                    >
                        {t('hero.title')}
                    </motion.h1>
                    <motion.p
                        className="hero-subtitle"
                        initial={{ y: 30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.4, duration: 0.8 }}
                    >
                        {t('hero.subtitle')}
                    </motion.p>
                    <motion.p
                        className="hero-description"
                        initial={{ y: 30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.6, duration: 0.8 }}
                    >
                        {t('hero.description')}
                    </motion.p>
                    <motion.button
                        className="hero-cta"
                        onClick={() => navigate('/products')}
                        initial={{ y: 30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.8, duration: 0.8 }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        {t('hero.cta')}
                    </motion.button>
                </div>
                <div className="hero-background">
                    <div className="hero-gradient"></div>
                </div>
            </motion.section>

            {/* Featured Products Section */}
            {/* <ServiceBadges /> */}
            <section className="featured-section">
                <div className="section-container">
                    <motion.div
                        className="section-header"
                        initial={{ y: 30, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="section-title">{t('products.title')}</h2>
                        <p className="section-subtitle">{t('products.subtitle')}</p>
                    </motion.div>

                    {loading ? (
                        <div className="loading-spinner">Loading...</div>
                    ) : (
                        <ProductCarousel
                            products={featuredProducts}
                            onProductClick={handleProductClick}
                        />
                    )}

                    <div className="section-cta">
                        <button
                            className="view-all-btn"
                            onClick={() => navigate('/products')}
                        >
                            {t('products.viewAll')}
                        </button>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="features-section">
                <div className="section-container">
                    <motion.div
                        className="features-grid"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <motion.div
                            className="feature-card"
                            whileHover={{ y: -10 }}
                            transition={{ duration: 0.3 }}
                        >
                            <div className="feature-icon">🏆</div>
                            <h3>{t('about.features.quality.title')}</h3>
                            <p>{t('about.features.quality.description')}</p>
                        </motion.div>

                        <motion.div
                            className="feature-card"
                            whileHover={{ y: -10 }}
                            transition={{ duration: 0.3 }}
                        >
                            <div className="feature-icon">🛡️</div>
                            <h3>{t('about.features.safety.title')}</h3>
                            <p>{t('about.features.safety.description')}</p>
                        </motion.div>

                        <motion.div
                            className="feature-card"
                            whileHover={{ y: -10 }}
                            transition={{ duration: 0.3 }}
                        >
                            <div className="feature-icon">💡</div>
                            <h3>{t('about.features.innovation.title')}</h3>
                            <p>{t('about.features.innovation.description')}</p>
                        </motion.div>

                        <motion.div
                            className="feature-card"
                            whileHover={{ y: -10 }}
                            transition={{ duration: 0.3 }}
                        >
                            <div className="feature-icon">🌍</div>
                            <h3>{t('about.features.global.title')}</h3>
                            <p>{t('about.features.global.description')}</p>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section">
                <div className="cta-content">
                    <h2>{t('contact.title')}</h2>
                    <p>{t('contact.subtitle')}</p>
                    <button
                        className="cta-button"
                        onClick={() => navigate('/contact')}
                    >
                        {t('nav.contact')}
                    </button>
                </div>
            </section>

            {/* Virtual Assistant */}
            {/* <ChatBot /> */}
        </div>
    );
};

export default HomePage;
